import striptags from "striptags"

import getIdealImage from "utils/getIdealImage"
import makeSecureImageUrl from "utils/makeSecureImageUrl"
import { applyImageCloudinaryTransforms } from "utils/applyCloudinaryTransforms"

const useSchema = (article) => {
  // add the schema
  let textArticleBody = ""
  const productReviews = []
  const schema = {
    "@context": "schema.org",
    "type": article.tags && article.tags.indexOf("news") > -1 ? "NewsArticle" : "Article",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": article.absoluteUrl,
    },
    "url": article.absoluteUrl,
    "headline": article.titles.site,
    "publisher": {
      "@type": "Organization",
      "name": "mindbodygreen",
      "url": "https://www.mindbodygreen.com",
      "logo": {
        "@type": "ImageObject",
        "url":
          "https://mindbodygreen-res.cloudinary.com/image/upload/w_800,h_800/mbg_logo_short-square-2x-v3.png",
        "width": "800",
        "height": "800",
      },
      "sameAs": [
        "https://www.facebook.com/mindbodygreen",
        "https://twitter.com/mindbodygreen",
        "https://www.pinterest.com/mindbodygreen/",
        "https://instagram.com/mindbodygreen",
        "https://www.youtube.com/mindbodygreen",
      ],
      "telephone": "+1 877-624-9355",
    },
  }
  if (article.useSocialPreview && article.previewImage) {
    schema.image = {
      "@type": "ImageObject",
      "url": getIdealImage(
        article.previewImage,
        "c_fill,w_1200,h_800,g_auto,q_90,fl_lossy,f_jpg",
        1200,
        800,
        null,
        false,
      ),
    }
  }
  if (article.publishDate) {
    schema.datePublished = new Date(article.publishDate).toISOString()
  }

  if (article.lastUpdate && article.publishDate) {
    if (
      new Date(article.publishDate) < new Date() &&
      new Date(article.lastUpdate) > new Date(article.publishDate)
    ) {
      schema.dateModified = new Date(article.lastUpdate).toISOString()
    } else {
      schema.dateModified = new Date(article.publishDate).toISOString()
    }
  }
  if (article.lastUpdate && !article.publishDate) {
    schema.dateModified = new Date(article.lastUpdate).toISOString()
  }

  // if there is a lastModified value, use it for the datePublished
  if (article.lastModified) {
    schema.datePublished = new Date(article.lastModified).toISOString()
  }

  const author = article.authors && article.authors[0]
  if (author) {
    schema.author = {
      "@type": "Person",
      "name": author.name,
      "sameAs": [`https://www.mindbodygreen.com/wc/${author.url}`],
    }
    if (author.twitter) {
      schema.author.sameAs.push(`https://twitter.com/${author.twitter}`)
    }
    if (author.website) {
      schema.author.url = author.website
    }
    if (author.instagram) {
      schema.author.sameAs.push(`https://instagram.com/${author.instagram}`)
    }
    if (author.facebookPage) {
      schema.author.sameAs.push(author.facebookPage)
    }
    if (author.heroImage) {
      schema.author.image = makeSecureImageUrl(
        author.heroImage,
        "c_fill,w_250,h_250,g_face:center,q_85,fl_lossy,f_jpg",
        author.name,
      )
    } else if (author.image) {
      schema.author.image = makeSecureImageUrl(
        author.image,
        "c_fill,w_250,h_250,g_face:center,q_85,fl_lossy,f_jpg",
        author.name,
      )
    }
  }

  const reviewer = article.reviewers && article.reviewers[0]
  if (reviewer && reviewer.type !== "fact-checked") {
    const reviewedBy = {
      "@type": "Person",
      "name": reviewer.name,
      "sameAs": [`https://www.mindbodygreen.com/wc/${reviewer.url}`],
    }
    try {
      const [name, ...honorific] = reviewer.name.split(",")
      if (name && honorific) {
        reviewedBy.name = name.trim()
        reviewedBy.honorificSuffix = honorific.reduce((existing, title) => {
          return existing.length ? `${existing}, ${title.trim()}` : title.trim()
        }, "")
      }
    } catch (err) {
      // ignore
    }
    if (reviewer.twitter) {
      reviewedBy.sameAs.push(`https://twitter.com/${reviewer.twitter}`)
    }
    if (reviewer.website) {
      reviewedBy.url = reviewer.website
    }
    if (reviewer.instagram) {
      reviewedBy.sameAs.push(`https://instagram.com/${reviewer.instagram}`)
    }
    if (reviewer.facebookPage) {
      reviewedBy.sameAs.push(reviewer.facebookPage)
    }

    try {
      if (reviewer.heroImage) {
        reviewedBy.image = makeSecureImageUrl(
          reviewer.heroImage,
          "c_fill,w_250,h_250,g_face:center,q_85,fl_lossy,f_jpg",
        )
      } else if (reviewer.image) {
        reviewedBy.image = makeSecureImageUrl(
          reviewer.image,
          "c_fill,w_250,h_250,g_face:center,q_85,fl_lossy,f_jpg",
        )
      }
    } catch (err) {
      // ignore
    }
    schema.mainEntityOfPage.reviewedBy = reviewedBy
    if (reviewer.date) {
      schema.mainEntityOfPage.lastReviewed = new Date(reviewer.date).toISOString()
    }
  }

  if (article.body) {
    // get enhanced list faq components for schema tags
    try {
      const faqs = article.body.filter(
        ({ type, style }) => type === "enhanced-list" && style === "faq",
      )
      if (faqs?.length) {
        const questions = []
        faqs.forEach(({ items }) => {
          items.forEach(({ heading, body, doNotLink }) => {
            let answer = body
            if (!doNotLink) {
              answer = `${answer} -- <a href="${article.absoluteUrl}">Read More</a>`
            }
            const question = {
              "@type": "Question",
              "name": striptags(heading),
              "acceptedAnswer": {
                "@type": "Answer",
                "text": answer,
              },
            }
            questions.push(question)
          })
        })
        schema.about = [
          {
            "@type": "FAQPage",
            "mainEntity": questions,
          },
        ]
      }
    } catch (err) {
      console.log(err)
    }
  }

  if (article.body) {
    article.body.forEach((component) => {
      try {
        // get schema body
        if (component.text) {
          // if there is text, use it
          textArticleBody += `${striptags(component.text)} `
        }
        if (component.heading) {
          // if there is a heading, use it
          textArticleBody += `${striptags(component.heading)} `
        }
        if (component.type === "code") {
          textArticleBody += `${striptags(component.code)} `
        }
        if (component.type === "gallery") {
          if (component.slides) {
            component.slides.forEach((slide) => {
              textArticleBody += `${striptags(slide.text)} `
              if (!slide.image.altText) {
                slide.image.altText = article.titles.site
              }
            })
          }
        }
        if (component.title) {
          textArticleBody += `${striptags(component.title)} `
        }
        if (component.type === "shop" && component.products) {
          component.products.forEach(({ brand, source, productName, description }) => {
            if (brand) {
              textArticleBody += `${striptags(brand)} `
            }
            if (source) {
              textArticleBody += `${striptags(source)} `
            }
            if (productName) {
              textArticleBody += `${striptags(productName)} `
            }
            if (description) {
              textArticleBody += `${striptags(description)} `
            }
          })
        }
        if (component.type === "enhanced-list") {
          const { style, title, items } = component
          if (style === "faq" && !title) {
            textArticleBody += "Frequently Asked Questions "
          }
          if (items) {
            items.forEach(({ heading, body }) => {
              textArticleBody += `${striptags(heading)} ${striptags(body)} `
            })
          }
        }
        if (component.type === "review") {
          const { items, quickView } = component
          if (items && !quickView) {
            items.forEach(
              ({ title, description, company, list, selectedCategories, image, sources }) => {
                textArticleBody += `${striptags(title)} ${striptags(company)} ${striptags(
                  description,
                )} `
                const productName = title
                const prosItemListElement = []
                const consItemListElement = []
                const reviewBody = description || ""
                const imageUrl = image?.path
                  ? applyImageCloudinaryTransforms(
                      image.path,
                      "c_fit,w_370,h_255,q_auto,fl_lossy,f_auto",
                    )
                  : null
                if (list) {
                  list.forEach(({ title: itemTitle, items: listItems }) => {
                    textArticleBody += `${striptags(title)} `
                    if (listItems) {
                      listItems.forEach(({ text }, index) => {
                        textArticleBody += `${striptags(text)} `
                        if (itemTitle?.match(/^pros/i)) {
                          prosItemListElement.push({
                            "@type": "ListItem",
                            "position": index + 1,
                            "name": text,
                          })
                        } else if (itemTitle?.match(/^cons/i)) {
                          consItemListElement.push({
                            "@type": "ListItem",
                            "position": index + 1,
                            "name": text,
                          })
                        }
                      })
                    }
                  })
                }
                if (selectedCategories) {
                  selectedCategories.forEach(({ title: catTitle, options }) => {
                    if (catTitle) {
                      textArticleBody += `${striptags(catTitle)} `
                    }
                    if (options) {
                      options.forEach(({ value, text }) => {
                        if (value) {
                          textArticleBody += `${striptags(text)} `
                        }
                      })
                    }
                  })
                }
                if (sources) {
                  // do not include any product without a source
                  const product = {
                    "@type": "Product",
                    "name": productName,
                    "image": imageUrl,
                    "URL": sources && sources.length ? sources[0].url : "",
                    "review": {
                      "@type": "Review",
                      "reviewBody": reviewBody.replace(/<[^>]*>/g, ""),
                      "author": {
                        "@type": "Person",
                        "name": author ? author.name : "",
                      },
                      "positiveNotes": {
                        "@type": "ItemList",
                        "itemListElement": prosItemListElement,
                      },
                      "negativeNotes": {
                        "@type": "ItemList",
                        "itemListElement": consItemListElement,
                      },
                    },
                  }
                  if (company) {
                    product.brand = {
                      "@type": "Brand",
                      "name": striptags(company),
                    }
                  }
                  productReviews.push(product)
                }
              },
            )
          }
        }
      } catch (err) {
        console.log(err)
      }
    })
  }

  if (productReviews.length) {
    if (productReviews.length) {
      const reviews = {
        "@context": "http://schema.org",
        "@type": "ItemList",
        "name": article.titles.site,
        "itemListOrder": "http://schema.org/ItemListUnordered",
        "numberOfItems": productReviews.length,
        "itemListElement": productReviews,
      }
      if (schema.about) {
        schema.about.push(reviews)
      } else {
        schema.about = [reviews]
      }
      schema.articleBody = textArticleBody

      return schema
    }
  }
  schema.articleBody = textArticleBody

  return schema
}

export default useSchema
